import type { Translator } from '@porotutu/i18n'
import type { ApiError } from '@porotutu/react/errors'
import type { FieldValues } from 'react-hook-form'
import type { FormBridge } from '../components/form'
import { notifyStale } from '../components/notify'
import { applyFailures } from './failures'

const hasKey = <K extends string, V = unknown>(value: unknown, key: K): value is Record<K, V> =>
  typeof value === 'object' && value !== null && key in value

export const lockTokenOf = (row: unknown): string | undefined => {
  if (!hasKey(row, 'updated_at')) return undefined

  const token = row.updated_at

  return typeof token === 'string' && token !== '' ? token : undefined
}

export const UNLOCKED: ApiError = {
  status: 409,
  value: { detail: { record: null } },
}

export function lockedBodyOf<Body>(body: Body, record: unknown): Body & { lock_token: string } {
  const lock_token = lockTokenOf(record)

  if (lock_token === undefined) throw UNLOCKED

  return { ...body, lock_token }
}

export function staleRecordOf<Row>(error: ApiError | null): Row | null {
  if (error?.status !== 409) return null

  if (!hasKey(error.value, 'detail')) return null

  const detail = error.value.detail

  if (!hasKey<'record', Row | null>(detail, 'record')) return null

  return detail.record ?? null
}

export function onConflictOf<Row, Values extends FieldValues>({
  seed,
  valuesOf,
  t,
  also,
}: {
  seed: (row: Row) => void
  valuesOf: (row: Row) => Values
  t: Translator
  also?: (row: Row) => void
}) {
  return (error: ApiError | null, form: FormBridge<Values>) => {
    const fresh = staleRecordOf<Row>(error)

    if (fresh === null) {
      if (error?.status === 409) notifyStale(t)
      else applyFailures(error, form.setError, t)

      return
    }

    seed(fresh)
    also?.(fresh)
    form.reload(valuesOf(fresh))
    notifyStale(t)
  }
}
